import { useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { useSession } from '../../state/session';
import { UiContainer } from '../../state/ui';
import { AccountPageNavigation } from '../AccountPageNavigation/AccountPageNavigation';
import { CheckmarkList } from '../CheckmarkList/CheckmarkList';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { ButtonLink } from '../Form/Button';
import { Icon, coins } from '../Icon/Icon';
import { Main } from '../Main/Main';
import { ProductListing } from '../ProductListing/ProductListing';
import { Routing } from '../Routes';
import { LoaderPage } from './LoaderPage';
import {
	StatisticsCategoriesSection,
	StatisticsChartSection,
	StatisticsConsumptionSection,
	StatisticsEcoSection,
	StatisticsSummarySection,
} from './statistics/StatisticsSections';
import './StatisticsPage.css';

export const StatisticsPage = (): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const routes = useTranslation().routes;
	const productT = useTranslation().product;
	const { user } = useSession();
	const { priceWithVat } = UiContainer.useContainer();
	const { data: filters, error: filtersError } = useApi(Res.statisticsPresets, {});
	const { data: mostBought } = useApi(user ? Res.mostBought : null, {});

	if (filtersError && !filters) {
		return (
			<Main>
				<ErrorMessage />
			</Main>
		);
	}

	if (!filters) {
		return <LoaderPage />;
	}

	if (!filters.presets.length || !filters.productTypes.length) {
		return (
			<Main>
				<ErrorMessage />
			</Main>
		);
	}
	const visibleMostBought = mostBought?.slice(0, 8);

	return (
		<Main>
			<div id="statistics-page">
				<div className="statistics-top-wrapper">
					<AccountPageNavigation />
					<h1 className="statistics-page-header">{t.heading}</h1>

					<StatisticsChartSection filters={filters} includeVat={priceWithVat} />
				</div>

				<StatisticsCategoriesSection filters={filters} includeVat={priceWithVat} />
				<StatisticsEcoSection filters={filters} includeVat={priceWithVat} />
				<StatisticsConsumptionSection filters={filters} includeVat={priceWithVat} />
				<StatisticsSummarySection
					filters={filters}
					includeVat={priceWithVat}
					hasSpecials={Boolean(user?.specials)}
				/>

				{!user?.specials ? (
					<section className="statistics-upsell-section">
						<div className="statistics-upsell-grid">
							<div className="statistics-upsell-card accent">
								<h2>{t.specialPricesTitle}</h2>
								<Icon shape={coins} size={68} />
							</div>
							<div className="statistics-upsell-card">
								<p>{t.specialPricesBody}</p>
								<CheckmarkList className="black compact">
									<li>{t.specialPricesBulletA}</li>
									<li>{t.specialPricesBulletB}</li>
									<li>{t.specialPricesBulletC}</li>
								</CheckmarkList>
								<ButtonLink className="text" linkTo={routes.specialPrices2}>
									<span>{t.specialPricesLink}</span>
									<span>→</span>
								</ButtonLink>
							</div>
						</div>
					</section>
				) : null}

				{visibleMostBought?.length ? (
					<section className="statistics-section statistics-products-section">
						<div className="statistics-section-heading">
							<h2>{productT.mostBoughtProducts}</h2>
						</div>
						<ProductListing products={visibleMostBought} />
					</section>
				) : null}
			</div>
		</Main>
	);
};

export const statisticsRouting: Routing = {
	path(urls) {
		return urls.statistics;
	},
	component: StatisticsPage,
	requiresAuth: true,
};
